import {
  EventEmitterWithIndex,
  TICKET_SOURCE,
  getTranslatedText,
  getCurrentUserHook
} from '@nile/components';
import {
  TicketClient,
  GET_DEFAULT_REPLY_TEMPLATE,
  GET_CLIENT_USERS,
  AccountClient
} from '@nile/graphql';
import { GET_TICKET_REPLY_RECIPEINTS } from '@nile/graphql/src/Query/ticket';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Button,
  Sheet,
  Badge,
  DisplayText,
  Tippy
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import { ReplyTemplateList } from '@nile/mangifera/src/Containers/ChatContainers/Helpers/helpers';
import { useTicketConversationActionStore } from '@nile/mangifera/src/StoreManager/store/TicketDetails/TicketConversationActionStore';
import TicketKnowledgeBase from '@nile/mangifera/src/Containers/ChatContainers/TicketKnowledgeBase';
import { dompurifier } from '@nile/components/src/Utils/helpers';
import { useSuperEditor } from '@nile/components/src/Components/RichTextEditor/useSuperEditor';
import { SelectUsers } from '@nile/mangifera/src/Pebbles/ChatContainer';
import { SuperopsAppReplyHeader } from '@nile/mangifera/src/Containers/ChatContainers/ActionContainerV2';
import TicketMailboxSelector from './TicketMailboxSelector';
import TicketAIRephrase from './TicketAIRephrase';

const TicketConversationHeaderAction = (props) => {
  const intl = useIntl();
  const { data, editorRef, mixpanelEventOnCopy, mixpanelEventOnClick } = props;

  const ticket = data?.data?.ticket;
  const mode = data?.data?.mode;
  const imConfig = data?.data?.imConfig;

  const currentUser = getCurrentUserHook();

  const isInstantMessaging = ticket?.source === TICKET_SOURCE.Communication;

  const editorConfig = data?.config?.editor?.storeConfig;
  const { updateTemplate, template } = useSuperEditor(editorConfig);
  const ticketId = ticket?.id;

  const ticketConversationStore = useTicketConversationActionStore({
    ticketId: ticketId
  });

  const conversationScope = ticketConversationStore(
    (state) => state.conversation?.conversationScope
  );

  const setConversationScope = ticketConversationStore(
    (state) => state.setConversationScope
  );

  const isPrivateChannel = conversationScope === 'PRIVATE';

  const selectedConversationMailbox = ticketConversationStore(
    (state) => state.conversation?.selectedMailbox
  );

  const setSelectedConversationMailbox = ticketConversationStore(
    (state) => state.setSelectedConversationMailbox
  );

  const conversationTo = ticketConversationStore(
    (state) => state.conversation?.to
  );
  const setConversationTo = ticketConversationStore(
    (state) => state.setConversationTo
  );

  const insertContentTimeOutRef = React.useRef(null);
  const replaceContentTimeOutRef = React.useRef(null);

  const replaceContent = (content) => {
    if (replaceContentTimeOutRef.current) {
      clearTimeout(replaceContentTimeOutRef.current);
    }
    replaceContentTimeOutRef.current = setTimeout(() => {
      EventEmitterWithIndex.dispatch('replaceEditorContent', content);
    }, 100);
  };

  const insertContent = (content) => {
    if (insertContentTimeOutRef.current) {
      clearTimeout(insertContentTimeOutRef.current);
    }
    insertContentTimeOutRef.current = setTimeout(() => {
      EventEmitterWithIndex.dispatch('insertEditorContent', content);
    }, 100);
  };

  const selectedMailbox = selectedConversationMailbox;

  const setSelectedMailbox = setSelectedConversationMailbox;

  const populateEditorContentAndAttachments = ({
    content,
    attachments,
    type
  }) => {
    if (type === 'reset') {
      replaceContent(content);
      if (attachments && attachments?.length > 0) {
        updateTemplate({
          attachments: attachments
        });
      }
    } else {
      insertContent(content);
      if (attachments && attachments.length > 0) {
        const currentAttachments = template?.attachments || [];
        updateTemplate({
          attachments: [...currentAttachments, ...attachments]
        });
      }
    }
  };

  const getConversationTemplate = (variables, onCompleted) => {
    TicketClient.query({
      query: GET_DEFAULT_REPLY_TEMPLATE,
      variables: variables
    }).then((res) => {
      if (res?.data?.getConversationContentFromReplyTemplate?.content) {
        const content =
          res?.data?.getConversationContentFromReplyTemplate?.content;
        const attachmentsToAdd =
          res?.data?.getConversationContentFromReplyTemplate?.attachments;
        if (onCompleted) {
          onCompleted({
            content,
            attachments: attachmentsToAdd
          });
        }
      }
    });
  };

  const onMailboxCompleted = (data) => {
    populateEditorContentAndAttachments({
      content: data?.content,
      attachments: data?.attachments,
      type: 'reset'
    });
  };

  const onMailboxChange = (value) => {
    setSelectedMailbox(value);
    if (mode === 'conversation') {
      const variables = {
        input: {
          ticket: {
            ticketId: ticketId
          },
          mailbox: {
            boxId: value.boxId
          }
        }
      };
      getConversationTemplate(variables, onMailboxCompleted);
    }
  };

  const [sheetActive, setSheetActive] = useState(false);

  const toggleSheetActive = () => {
    setSheetActive((prev) => !prev);
  };

  const onReplyTemplateCompleted = (data) => {
    populateEditorContentAndAttachments({
      content: data?.content,
      attachments: data?.attachments
    });
  };

  const populateReplyTemplate = (templateData) => {
    if (mode === 'conversation') {
      const templateId = templateData?.id;
      if (templateId) {
        const variables = {
          input: {
            ticket: {
              ticketId: ticketId
            },
            template: {
              templateId: templateId
            },
            mailbox: selectedMailbox.boxId
              ? {
                  boxId: selectedMailbox.boxId
                }
              : undefined
          }
        };
        getConversationTemplate(variables, onReplyTemplateCompleted);
      }
    }
  };

  const formatOptions = (res) => {
    return res?.getTicketConversationReplyRecipients?.mailboxes?.map((item) => {
      return {
        ...item,
        label: item?.outgoingAddress,
        value: item?.outgoingAddress
      };
    });
  };

  const onArticleCopy = (content) => {
    const sanitizedContent = dompurifier(content);
    insertContent(sanitizedContent);
  };
  const generateReceiverOptions = (users) => {
    const requester = ticket?.requester || null;
    const additionalRequesters = ticket?.additionalRequester || [];
    const currentUserId = currentUser?.userId;

    //  Filter users with roleType TECHNICIAN and not the current user
    const technicianUsers =
      users?.filter(
        (user) =>
          user?.user?.role?.roleType?.type === 'TECHNICIAN' &&
          user?.user?.userId !== currentUserId
      ) || [];
    // Get requester and additionalRequesters, filtering out the current user
    const otherRequesters = [requester, ...additionalRequesters]
      ?.filter((user) => user && user?.userId && user?.userId !== currentUserId)
      .map((user) => {
        return {
          user: user
        };
      });
    // Merge techs and requesters
    const allFilterUsers = [...technicianUsers, ...otherRequesters];
    // Deduplicate by userId
    const uniqueUsers = allFilterUsers?.filter(
      (user, index, self) =>
        index === self.findIndex((u) => u?.user?.userId === user?.user?.userId)
    );
    return uniqueUsers?.map((user) => {
      return {
        ...user,
        label: user?.user?.name,
        value: user?.user?.email
      };
    });
  };

  const togglePrivateChannel = () => {
    const update = conversationScope === 'PRIVATE' ? 'PUBLIC' : 'PRIVATE';
    setConversationScope(update);
  };

  const updateToUsers = (users) => {
    setConversationTo(users);
  };

  return (
    <div
      className="flex actr fx-sb tw-gap-x-2 tw-mb-2"
      id="ticket-conversation-header-action"
    >
      <Sheet open={sheetActive} onClose={toggleSheetActive}>
        <ReplyTemplateList
          populateTemplate={populateReplyTemplate}
          toggleSheetActive={toggleSheetActive}
        />
      </Sheet>

      <div className="w100">
        {isInstantMessaging ? (
          <>
            {imConfig?.supportedChannelType === 'DM' ? (
              <div className="w100">
                <SuperopsAppReplyHeader label={ticket?.requester} />
              </div>
            ) : (
              <div className="flex actr tw-gap-x-2">
                <div
                  className="flex actr tw-gap-x-1"
                  onClick={togglePrivateChannel}
                >
                  <Badge
                    radial
                    size="small"
                    status={!isPrivateChannel && 'success'}
                    icon={
                      <SVGIcon
                        name={
                          isPrivateChannel
                            ? 'PrivateArticlePlain'
                            : 'GlobeSharedView'
                        }
                      />
                    }
                  />

                  <DisplayText size="large" element="p">
                    {isPrivateChannel ? 'Private' : 'Public'}
                  </DisplayText>

                  <div className="flex">
                    <SVGIcon name="ArrowDown" />
                  </div>
                </div>

                <div className="w100">
                  {isPrivateChannel && (
                    <SelectUsers
                      updateField={updateToUsers}
                      property="recipientId"
                      query={GET_CLIENT_USERS}
                      params={{
                        client: AccountClient,
                        variables: {
                          input: { clientId: ticket?.client?.accountId }
                        }
                      }}
                      methodName="getClientUserAssociationsList"
                      generateOptions={generateReceiverOptions}
                      isMulti
                      field="recipients"
                      placeholder={getTranslatedText(
                        intl,
                        'common.choose.technician'
                      )}
                      styles={{
                        multiValueRemove: (base, state) => {
                          if (state.data.isFixed) {
                            return { ...base, display: 'none' };
                          }
                          return base;
                        }
                      }}
                      isClearable={false}
                      value={conversationTo}
                      noMargin
                    />
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <TicketMailboxSelector
            value={selectedMailbox}
            onChange={onMailboxChange}
            client={TicketClient}
            query={GET_TICKET_REPLY_RECIPEINTS}
            formatOptions={formatOptions}
            variables={{
              ticketInput: {
                ticketId: ticketId
              }
            }}
            noBorder
          />
        )}
      </div>

      <div className="flex actr tw-gap-x-2">
        <TicketAIRephrase
          editorRef={editorRef}
          ticket={ticket}
          mixpanelEventOnCopy={mixpanelEventOnCopy}
          mixpanelEventOnClick={mixpanelEventOnClick}
        />

        {mode === 'conversation' && (
          <TicketKnowledgeBase
            ticketId={ticket?.id}
            onArticleCopy={onArticleCopy}
          />
        )}

        {!isInstantMessaging && (
          <Tippy
            title={getTranslatedText(
              intl,
              'ticket.details.chat.replyTemplate.header'
            )}
            inertia
            trigger="mouseenter"
            animation="shift"
            animateFill={false}
            position="bottom"
            arrow
          >
            <Button
              plain
              onClick={toggleSheetActive}
              disabled={!selectedMailbox}
              icon={<SVGIcon name="ReplyBubble" />}
            />
          </Tippy>
        )}
      </div>
    </div>
  );
};

export default TicketConversationHeaderAction;
